/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import { CrossTabCoordinator, DEFAULT_CROSS_TAB_CONFIG } from "../app/session/CrossTabCoordinator";
import { forceRefreshSessionAsync } from "../app/sessionSlice";
import store from "../app/store";
import getConfig from "../config";
import { clearLocalStorage, isOneWebDomainRuntime, retainCriticalData } from "./Utils";
import { cleanupSessionRefreshTimeout } from "./sessionTimeoutCleanup";

// Config is retrieved lazily inside functions to support runtime configuration
const getEcmApiHost = () => getConfig().hostnames.ecmApiHost;

export const getPageTitle = pageTitle => (dispatch, getState) => {
    const { session } = getState();
    return `AvaTax | ${session.activeCompany.name} | ${pageTitle}`;
};

export const getSessionAccessToken = () => {
    return new Promise((resolve, reject) => {
        const MAX_RETRY_ATTEMPTS = 6;
        const INITIAL_DELAY_MS = 100;
        const MAX_DELAY_MS = 2000;
        let attemptCount = 0;
        let isSettled = false;

        const checkTokenAvailability = () => {
            if (isSettled) {
                return;
            }

            attemptCount++;
            const { session } = store.getState();

            // Fast-fail on explicit authentication failure
            if (session?.isAuthenticated === false && session?.isLoading === false) {
                isSettled = true;
                reject(new Error("Authentication failed - user not authenticated"));
                return;
            }

            const isSessionReady =
                session?.accessToken &&
                session?.activeCompany?.id &&
                session?.isLoading === false &&
                session?.isAuthenticated === true;

            if (isSessionReady) {
                isSettled = true;
                resolve({ accessToken: session.accessToken });
                return;
            }

            if (attemptCount >= MAX_RETRY_ATTEMPTS && !isOneWebDomainRuntime()) {
                isSettled = true;
                // Try one final session refresh before giving up
                // Use false for user-initiated refresh, true to bypass minimum interval for retry mechanism
                store
                    .dispatch(forceRefreshSessionAsync(false, true))
                    .then(() => {
                        const { session: refreshedSession } = store.getState();
                        if (refreshedSession?.accessToken && refreshedSession?.isAuthenticated) {
                            resolve({ accessToken: refreshedSession.accessToken });
                        } else {
                            const errorMessage = `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts. Current state: ${JSON.stringify(
                                {
                                    hasAccessToken: !!refreshedSession?.accessToken,
                                    hasCompanyId: !!refreshedSession?.activeCompany?.id,
                                    isLoading: refreshedSession?.isLoading,
                                    isAuthenticated: refreshedSession?.isAuthenticated,
                                }
                            )}`;
                            reject(new Error(errorMessage));
                        }
                    })
                    .catch(() => {
                        const errorMessage = `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts. Current state: ${JSON.stringify(
                            {
                                hasAccessToken: !!session?.accessToken,
                                hasCompanyId: !!session?.activeCompany?.id,
                                isLoading: session?.isLoading,
                                isAuthenticated: session?.isAuthenticated,
                            }
                        )}`;
                        reject(new Error(errorMessage));
                    });
                return;
            }

            const delay = Math.min(INITIAL_DELAY_MS * Math.pow(2, attemptCount - 1), MAX_DELAY_MS);

            setTimeout(checkTokenAvailability, delay);
        };

        checkTokenAvailability();
    });
};

let crossTabCoordinator;
try {
    crossTabCoordinator = CrossTabCoordinator.getInstance(DEFAULT_CROSS_TAB_CONFIG);
} catch {
    // If coordinator initialization fails, set to null
    // The session refresh functions will handle this gracefully
    // eslint-disable-next-line no-unused-vars
    crossTabCoordinator = null;
}

export const redirectToLoginPage = () => {
    // Clean up session refresh timeout to prevent memory leaks
    cleanupSessionRefreshTimeout();

    // Step 1: Preserve current URL for post-login redirect
    // Critical for User Experience: Ensures user returns to the correct context
    // after successful login. Uses sessionStorage (tab-specific) rather than
    // localStorage (shared across tabs) to preserve each tab's individual context.
    let redirectUrl = window.location.href;
    try {
        // Store the current URL in sessionStorage for this specific tab
        sessionStorage.setItem("redirect_url", redirectUrl);
        // Retrieve it back to ensure storage succeeded (defensive programming)
        redirectUrl = sessionStorage.getItem("redirect_url") || redirectUrl;
    } catch {
        // Handle sessionStorage failures gracefully:
        // - Private browsing mode (sessionStorage may be disabled)
        // - Storage quota exceeded
        // - Security restrictions
        //
        // Fallback: Use current URL directly without storage
        // User will still be redirected correctly, just without persistence
    }

    // Step 2: Prepare for clean authentication session
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);

    // Retain critical application data that should survive logout
    retainCriticalData();

    // Clear localStorage to remove stale session data
    // This prevents conflicts between old and new authentication sessions
    clearLocalStorage();

    // Step 3: Perform the actual redirect to login
    // The redirectUrl parameter ensures the user returns to the correct context
    // after successful authentication. The login service will use this URL
    // to redirect the user back to their original location.
    window.location.href = `//${getEcmApiHost()}/api/v3/auth/login?redirectUrl=${encodedRedirectUrl}`;
};
